import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalDateKey } from './dateUtils';
import { ScheduleAddTaskActions } from './ScheduleAddTaskActions';
import { getScheduleBuckets } from './scheduleBuckets';
import { ScheduleHabitCard } from './ScheduleHabitCard';
import { ScheduleSection } from './ScheduleSection';
import { ScheduleTaskCard } from './ScheduleTaskCard';
import { ScheduleUpcoming } from './ScheduleUpcoming';
import { ScheduleViewProps } from './scheduleViewTypes';
import { PlannerTaskInfo } from './types';

type SectionKey = 'overdue' | 'today' | 'upcoming';

export const ScheduleView: React.FC<ScheduleViewProps> = ({
    tasks, habitsForToday, getFolderName, getCollectionName, onOpenTask, onOpenTaskInCollection, onEditTask,
    onToggleComplete, onToggleSubtask, onDeleteTask, onCreateTask, onPickExistingTask, onAdvanceHabit,
    onRescheduleTask,
}) => {
    const { t, i18n } = useTranslation();
    const [addMenuDate, setAddMenuDate] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>({
        overdue: true, today: false, upcoming: false,
    });
    const today = new Date();
    const buckets = getScheduleBuckets(tasks, today);
    const toggle = (key: SectionKey) => () => {
        setCollapsed((current) => ({ ...current, [key]: !current[key] }));
    };
    const getSourceLabel = useCallback((folderId: string, collectionId: string) => {
        const folderName = getFolderName(folderId);
        const collectionName = getCollectionName(collectionId);
        return folderName.trim().toLocaleLowerCase(i18n.language) === 'general'
            ? collectionName : `${folderName} / ${collectionName}`;
    }, [getCollectionName, getFolderName, i18n.language]);
    const taskProps = {
        getSourceLabel, onDeleteTask, onEditTask, onOpenTask, onOpenTaskInCollection, onRescheduleTask,
        onToggleComplete, onToggleSubtask,
    };
    const renderTask = (
        taskInfo: PlannerTaskInfo,
        options: { canReschedule?: boolean; isOverdue?: boolean } = {}
    ) => (
        <ScheduleTaskCard key={taskInfo.task.id} {...taskProps} taskInfo={taskInfo}
            isOverdue={options.isOverdue} showScheduledDate={options.canReschedule ?? true} />
    );
    const addActions = (date?: Date) => (
        <ScheduleAddTaskActions date={date} menuKey={addMenuDate} onMenuChange={setAddMenuDate}
            onCreateTask={onCreateTask} onPickExistingTask={onPickExistingTask} />
    );
    const upcomingGroups = [
        { key: 'week', label: t('dailyPlanner.thisWeek'), tasks: buckets.thisWeek },
        { key: 'next-week', label: t('dailyPlanner.nextWeek'), tasks: buckets.nextWeek },
        { key: 'month', label: t('dailyPlanner.thisMonth'), tasks: buckets.thisMonth },
        { key: 'next-month', label: t('dailyPlanner.nextMonth'), tasks: buckets.nextMonth },
        { key: 'year', label: t('dailyPlanner.thisYear'), tasks: buckets.thisYear },
        { key: 'future', label: t('dailyPlanner.futureYears'), tasks: buckets.future },
    ];
    const upcomingCount = upcomingGroups.reduce((total, group) => total + group.tasks.length, 0);
    return (
        <div className="schedule-view schedule-agenda">
            {buckets.overdue.length > 0 && (
                <ScheduleSection className="schedule-overdue" collapsed={collapsed.overdue}
                    label={t('dailyPlanner.overdue')} onToggle={toggle('overdue')}
                    total={buckets.overdue.length}>
                    <div className="schedule-tasks">{buckets.overdue.map((task) => renderTask(task, { isOverdue: true }))}</div>
                </ScheduleSection>
            )}
            <ScheduleSection className="schedule-today" collapsed={collapsed.today}
                label={t('dailyPlanner.today')} onToggle={toggle('today')}
                total={habitsForToday.length + buckets.today.length}>
                {habitsForToday.length === 0 && buckets.today.length === 0
                    ? <p className="schedule-today-empty">{t('dailyPlanner.noTodayItems')}</p>
                    : (
                    <div className="schedule-today-elements">
                        {habitsForToday.map((taskInfo) => (
                            <ScheduleHabitCard key={taskInfo.habit.id} taskInfo={taskInfo}
                                todayKey={getLocalDateKey(today)} getSourceLabel={getSourceLabel}
                                onAdvanceHabit={onAdvanceHabit} />
                        ))}
                        {buckets.today.map((task) => renderTask(task, { canReschedule: false }))}
                    </div>
                    )}
            </ScheduleSection>
            {upcomingCount > 0 && (
                <ScheduleSection className="schedule-upcoming" collapsed={collapsed.upcoming}
                    label={t('dailyPlanner.upcoming')} onToggle={toggle('upcoming')} total={upcomingCount}>
                    <ScheduleUpcoming groups={upcomingGroups} renderTask={renderTask} />
                </ScheduleSection>
            )}
            <div className="schedule-floating-add">{addActions()}</div>
        </div>
    );
};
